import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { OrderTypes } from "@shared/features/trades/common/types";
import { getItemValidations } from "@shared/features/configuration/utils";
import {
  getParsedStrategyValue,
  validateDcaStrategy,
} from "@shared/features/bots/BotForm/common/utils";
import {
  BotFormType,
  ChannelValue,
  GridType,
  InitializeDCAStrategyValues,
  MultiToggleValue,
  MultiValue,
  ParsedDcaStrategyData,
  StrategyBaseline,
  ToggleField,
  useDCAStrategyFormProps,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { STRATEGY_BASELINE_TO_DISPLAY } from "@shared/features/bots/BotForm/common/constants";
import { BotType } from "@shared/types";
import { BotBaseStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotBaseStrategiesProvider";
import { findLastNonZeroValue } from "@shared/utils/arrays";

const useSharedDCAStrategyForm = ({
  isCustomStrategy,
  orderType,
  createDcaStrategyTemplate,
  targets,
}: useDCAStrategyFormProps) => {
  const {
    botFormType,
    botType,
    gridsData,
    hedgingGridsData,
    longShort,
    symbolPriceData,
    checkedFields,
    isChannelConfiguration,
  } = useContext(BotFormContext) || {};
  const {
    numOrdersSelectorData,
    numActiveOrdersSelectorData,
    priceDifferenceSelectorData,
    priceScaleSelectorData,
    getBaseSelectorData,
    maxPriceDifferenceData,
  } = useContext(BotBaseStrategiesContext) || {};
  const isBulkEditBots = botFormType === BotFormType.BULK_EDIT;
  const isEntry = orderType === OrderTypes.ENTRY;
  const ignorePriceStrategy = !checkedFields?.has(
    isEntry
      ? ToggleField.ENTRY_PRICE_STRATEGY
      : ToggleField.TAKE_PROFIT_PRICE_STRATEGY
  );
  const ignoreRatios = !checkedFields?.has(
    isEntry ? ToggleField.ENTRY_RATIOS : ToggleField.TAKE_PROFIT_RATIOS
  );
  const ignoreFirstOrderPercent =
    (!checkedFields?.has(ToggleField.FIRST_ENTRY) && !isBulkEditBots) ||
    ignoreRatios;
  const currentPrice = symbolPriceData?.data || 0;
  const pricePrecision = symbolPriceData?.precision.price || 0;
  const inputRef = useRef<any>(null);
  const { max_amount_scale } =
    getItemValidations(createDcaStrategyTemplate) || {};

  const [firstOrderPercent, setFirstOrderPercent] = useState<
    string | undefined | MultiValue | MultiToggleValue
  >(undefined);
  const [amountScale, setAmountScale] = useState<
    string | undefined | MultiValue
  >();
  const [baseline, setBaseline] = useState<
    StrategyBaseline | undefined | MultiValue | ChannelValue
  >(isChannelConfiguration ? undefined : StrategyBaseline.PREVIOUS_ORDER);

  const priceDifference = priceDifferenceSelectorData?.rawValue;
  const priceScale = priceScaleSelectorData?.rawValue;
  const numOrders = numOrdersSelectorData?.rawValue;
  const numActiveOrders = numActiveOrdersSelectorData?.rawValue;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const activeGridsData = isHedgingBot ? hedgingGridsData : gridsData;
  const includeNumOrders = !isCustomStrategy && !isChannelConfiguration;
  const includeNumActiveOrders =
    isEntry && botType !== BotType.TRADING_VIEW_SIGNALS;
  const includeFirstOrderPercent =
    !isCustomStrategy && isEntry && !isGridBot && !isHedgingBot;

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const initializeValues: InitializeDCAStrategyValues = (
    amountScale,
    first_order_percentage,
    baseline = StrategyBaseline.PREVIOUS_ORDER
  ) => {
    const getParsedValue = (value?: any) =>
      isSymbol(value) ? value : value?.toString();
    setAmountScale(getParsedValue(amountScale));
    setFirstOrderPercent(getParsedValue(first_order_percentage));
    setBaseline(baseline);
  };

  const parsedData: ParsedDcaStrategyData = useMemo(() => {
    const parsedNumOrders =
      isCustomStrategy && targets
        ? findLastNonZeroValue(targets).toString()
        : numOrders;
    return {
      numOrders: getParsedStrategyValue(parsedNumOrders),
      numActiveOrders: getParsedStrategyValue(numActiveOrders),
      amountScale: getParsedStrategyValue(amountScale),
      priceDifference: getParsedStrategyValue(priceDifference),
      priceScale: getParsedStrategyValue(priceScale),
      firstOrderPercent: getParsedStrategyValue(firstOrderPercent),
      baseline,
    };
  }, [
    numOrders,
    numActiveOrders,
    amountScale,
    priceDifference,
    priceScale,
    ignoreFirstOrderPercent,
    ignorePriceStrategy,
    firstOrderPercent,
    baseline,
    targets,
  ]) as ParsedDcaStrategyData;

  const { isValid, firstOrderPercentError, isAmountScaleValid } =
    useMemo(() => {
      if (isChannelConfiguration)
        return {
          isValid: true,
          firstOrderPercentError: undefined,
          isAmountScaleValid: true,
        };
      return validateDcaStrategy(
        orderType,
        parsedData,
        isCustomStrategy,
        createDcaStrategyTemplate,
        includeNumOrders,
        includeNumActiveOrders,
        includeFirstOrderPercent,
        ignoreFirstOrderPercent,
        ignorePriceStrategy,
        longShort,
        currentPrice,
        pricePrecision,
        activeGridsData,
        botType
      );
    }, [
      orderType,
      parsedData,
      isCustomStrategy,
      createDcaStrategyTemplate,
      includeNumOrders,
      includeNumActiveOrders,
      includeFirstOrderPercent,
      ignoreFirstOrderPercent,
      ignorePriceStrategy,
      longShort,
      currentPrice,
      pricePrecision,
      activeGridsData,
      botType,
    ]);

  const firstOrderPercentSelectorData = {
    ...getBaseSelectorData?.("firstOrderPercent", firstOrderPercent),
    onChange: setFirstOrderPercent,
    show: includeFirstOrderPercent,
    isDefaultTakeProfit: false,
    error: firstOrderPercentError,
    precision: 2,
  };

  const amountScaleSelectorData = {
    ...getBaseSelectorData?.("amountScale", amountScale),
    onChange: setAmountScale,
    show: !isCustomStrategy,
    maxValue: max_amount_scale,
    minValue: 0.001,
    step: 0.1,
    precision: 3,
    showError: !isAmountScaleValid,
  };

  const baselineSelectorData = {
    ...getBaseSelectorData?.("baseline", baseline),
    onChange: setBaseline,
    show:
      (isGridBot || isHedgingBot) &&
      activeGridsData?.gridType === GridType.CUSTOM,
    options: [
      StrategyBaseline.PREVIOUS_ORDER,
      StrategyBaseline.FIRST_ORDER,
    ].map((baseline) => {
      return {
        value: baseline,
        name: STRATEGY_BASELINE_TO_DISPLAY[baseline],
      };
    }),
  };

  return {
    numOrdersSelectorData,
    numActiveOrdersSelectorData,
    firstOrderPercentSelectorData,
    amountScaleSelectorData,
    priceDifferenceSelectorData,
    priceScaleSelectorData,
    baselineSelectorData,
    initializeValues,
    isValid,
    parsedData,
    maxPriceDifferenceData,
  };
};

export default useSharedDCAStrategyForm;
